import { Store } from '@tanstack/react-store'
import { ApiClient, type SessionResponse } from '../lib/api/client.ts'

// User roles from JWT token (set by custom_access_token_hook in Supabase)
export type UserRole = 'admin' | 'teacher_admin' | 'teacher' | 'student'

/** Teachers and the Lehrerleitung: their portal lives in the admin app. */
export function isTeacherRole(role: UserRole): boolean {
  return role === 'teacher' || role === 'teacher_admin'
}

// Simplified user type for the BFF architecture (no Supabase SDK dependency)
export interface AuthUser {
  id: string
  email: string
  email_confirmed_at: string | null
  user_metadata: Record<string, unknown>
}

export interface AuthState {
  user: AuthUser | null
  userRole: UserRole
  phoneNumber: string
  verificationSid: string | null
  isVerifying: boolean
  isLoading: boolean
  error: string | null
  emailVerified: boolean
  phoneVerified: boolean
  isRecoverySession: boolean
  didSetPassword: boolean
}

export const authStore = new Store<AuthState>({
  user: null,
  userRole: 'student',
  phoneNumber: '',
  verificationSid: null,
  isVerifying: false,
  isLoading: true,
  error: null,
  emailVerified: false,
  phoneVerified: false,
  isRecoverySession: false,
  didSetPassword: true,
})

const isBrowser = typeof window !== 'undefined'

/**
 * Apply session response data to auth store
 */
function applySessionToStore(session: SessionResponse) {
  if (!session.user) {
    authStore.setState(state => ({
      ...state,
      user: null,
      userRole: 'student',
      emailVerified: false,
      phoneVerified: false,
      isRecoverySession: false,
      didSetPassword: true,
      isLoading: false,
    }))
    return
  }

  const userRole = (session.userRole || 'student') as UserRole

  authStore.setState(state => ({
    ...state,
    user: session.user,
    userRole,
    emailVerified: session.emailVerified ?? !!session.user!.email_confirmed_at,
    phoneVerified: session.phoneVerified ?? !!(session.user!.user_metadata as Record<string, unknown>)?.phone_verified,
    isRecoverySession: false,
    didSetPassword: true,
    isLoading: false,
  }))
}

// Initialize auth state from server session cookie (client-side only)
if (isBrowser) {
  // Check for recovery mode from query params (set by /api/auth/callback)
  const urlParams = new URLSearchParams(window.location.search)
  const isRecovery = urlParams.get('recovery') === 'true'

  ApiClient.getSession()
    .then((session) => {
      console.log('[AUTH STORE INIT] Session:', { hasUser: !!session.user })

      if (isRecovery && session.user) {
        // In recovery mode, mark as recovery session
        authStore.setState(state => ({
          ...state,
          user: session.user,
          userRole: (session.userRole || 'student') as UserRole,
          emailVerified: false, // Don't set during recovery
          phoneVerified: false,
          isRecoverySession: true,
          didSetPassword: true,
          isLoading: false,
        }))
      } else {
        applySessionToStore(session)
      }
    })
    .catch(err => {
      console.error('[AUTH STORE INIT] Failed to get session:', err)
      authStore.setState(state => ({
        ...state,
        isLoading: false,
      }))
    })
}

// Actions
export const authActions = {
  setUser: (user: AuthUser | null) => {
    authStore.setState(state => ({
      ...state,
      user,
      emailVerified: !!user?.email_confirmed_at,
      phoneVerified: !!(user?.user_metadata as Record<string, unknown> | undefined)?.phone_verified,
    }))
  },

  /**
   * Refresh session from server (call after sign-in, sign-up, etc.)
   */
  refreshSession: async () => {
    try {
      const session = await ApiClient.getSession()
      applySessionToStore(session)
    } catch (err) {
      console.error('[AUTH STORE] Failed to refresh session:', err)
    }
  },

  setPhoneNumber: (phoneNumber: string) => {
    authStore.setState(state => ({ ...state, phoneNumber }))
  },

  setVerificationSid: (verificationSid: string) => {
    authStore.setState(state => ({ ...state, verificationSid }))
  },

  setVerifying: (isVerifying: boolean) => {
    authStore.setState(state => ({ ...state, isVerifying }))
  },

  setError: (error: string | null) => {
    authStore.setState(state => ({ ...state, error }))
  },

  clearError: () => {
    authStore.setState(state => ({ ...state, error: null }))
  },

  setEmailVerified: (emailVerified: boolean) => {
    authStore.setState(state => ({ ...state, emailVerified }))
  },

  setPhoneVerified: (phoneVerified: boolean) => {
    authStore.setState(state => ({ ...state, phoneVerified }))
  },

  reset: () => {
    authStore.setState(state => ({
      ...state,
      phoneNumber: '',
      verificationSid: null,
      isVerifying: false,
      error: null,
    }))
  },

  clearRecoverySession: () => {
    authStore.setState(state => ({
      ...state,
      isRecoverySession: false,
    }))
  },

  clearAuth: () => {
    authStore.setState(state => ({
      ...state,
      user: null,
      userRole: 'student',
      emailVerified: false,
      phoneVerified: false,
      isRecoverySession: false,
    }))
  },
}
